import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useMe } from "./useMe";

/**
 * Gate the app on a successful `me`.
 *
 * There is no "logged out" branch to render: `client`'s 401 middleware has
 * already sent the browser to `/entrar` by the time an error lands here. What
 * this handles is the gap in between — and the case where `me` fails for some
 * other reason, which must say so rather than render an empty CRM.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { isPending, isError } = useMe();

  if (isPending) {
    return (
      <div className="grid min-h-dvh place-items-center bg-ground text-mute-600">
        {t("common.loading")}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="grid min-h-dvh place-items-center bg-ground p-6 text-center text-ink">
        <div>
          <p className="font-display text-lg font-extrabold">{t("auth.failed")}</p>
          <p className="mt-2 text-sm text-mute-600">{t("auth.failedHint")}</p>
          <button
            type="button"
            className="btn btn-primary mt-4"
            onClick={() => window.location.reload()}
          >
            {t("common.retry")}
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
